import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, EffectRef, inject, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet, UrlSegment } from '@angular/router';
import { PageHeaderComponent } from '@libs/common/components';
import { DocumentCategory, DocumentCountsModel, hasDocumentsOfCategory } from '@libs/services/models';
import { AuthService } from '@libs/shared/auth';
import { CapabilityService } from '@libs/shared/services';
import { NavItemModel, NavModel } from '@libs/shared/models';
import { DocumentsSummary } from '@libs/shared/services';
import { DeferredPageInfoTracking } from '@libs/shared/tracking';
import { catchError, EMPTY, filter, map, Observable, of, startWith, switchMap } from 'rxjs';
import { AccountDocumentNavItems, AccountMainUserNavigation, AccountNavigation } from './account.navigation';
import { CustomerNavigationComponent } from './customer';

import { AccountNavigationComponent } from './navigation';
import { MainUserService } from './services';

@Component({
  selector: 'ui-account',
  templateUrl: './account.component.html',
  styleUrls: ['./account.component.scss'],
  imports: [
    AccountNavigationComponent,
    RouterOutlet,
    CustomerNavigationComponent,
    NgClass,
    PageHeaderComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  // Holds the pageInfo event of the account pages that rewrite their own url, see `showCustomerNavigation`.
  providers: [
    DeferredPageInfoTracking,
  ],
})
export class AccountComponent {
  private readonly auth: AuthService = inject(AuthService);
  private readonly mainUserService: MainUserService = inject(MainUserService);
  private readonly deferredPageInfoTracking: DeferredPageInfoTracking = inject(DeferredPageInfoTracking);
  private readonly router: Router = inject(Router);
  private readonly route: ActivatedRoute = inject(ActivatedRoute);
  private readonly capabilityService: CapabilityService = inject(CapabilityService);
  private readonly documentsSummary: DocumentsSummary | null = inject(DocumentsSummary, { optional: true });

  private readonly navigationEnd$: Observable<NavigationEnd | undefined> = this.router.events.pipe(
    filter((event: unknown) => event instanceof NavigationEnd),
    startWith(undefined),
  );

  private readonly documentCounts: Signal<DocumentCountsModel | undefined> = toSignal(
    this.documentsSummary?.getDocumentCounts().pipe(catchError(() => of(undefined))) ?? of(undefined),
  );

  /**
   * Only the pages that show the customer navigation can still be redirected to a url with a customer id;
   * `CustomerNavigationComponent` reports those once it knows it is staying. Every other account page is
   * already on the url that describes it.
   */
  private readonly pageInfoEffect: EffectRef = effect((): void => {
    if (!this.showCustomerNavigation()) {
      this.deferredPageInfoTracking.complete();
    }
  });

  public readonly authorized: Signal<boolean | undefined> = toSignal(this.auth.isUserAuthorized$());
  public readonly mainUser: Signal<boolean> = toSignal(this.mainUserService.isMainUserForCurrentCustomer(), { initialValue: false });

  public readonly navigation: Signal<NavModel> = computed(() => this.appendDocumentNavItems(
    this.mainUser() && this.capabilityService.isAvailable('account.employeeAdministration')() ? AccountMainUserNavigation : AccountNavigation,
    this.documentCounts(),
  ));

  public readonly title: Signal<string | undefined> = toSignal(this.navigationEnd$.pipe(
    switchMap(() => this.route.firstChild?.title ?? EMPTY),
  ));

  public readonly currentPath: Signal<string | undefined> = toSignal(this.navigationEnd$.pipe(
    switchMap(() => this.route.firstChild?.url ?? EMPTY),
    map((segments: UrlSegment[]) => segments[0]?.path),
  ));

  public readonly isAtAccountRoot: Signal<boolean> = toSignal(this.navigationEnd$.pipe(
    map(() => {
      const firstChild: ActivatedRoute | null = this.route.firstChild;

      return !firstChild || firstChild.snapshot?.routeConfig?.path === '';
    }),
  ), {
    initialValue: !this.route.firstChild || this.route.firstChild.snapshot?.routeConfig?.path === '',
  });

  public readonly showCustomerNavigation: Signal<boolean> = computed(() =>
    ['customers'].includes(this.currentPath() ?? ''),
  );

  private appendDocumentNavItems(navigation: NavModel, counts: DocumentCountsModel | undefined): NavModel {
    if (!counts) {
      return navigation;
    }

    const documentNavItems: NavItemModel[] = (Object.keys(AccountDocumentNavItems) as DocumentCategory[])
      .filter((category: DocumentCategory) => hasDocumentsOfCategory(counts, category))
      .map((category: DocumentCategory) => AccountDocumentNavItems[category]);

    return documentNavItems.length > 0
      ? { ...navigation, children: [...navigation.children ?? [], ...documentNavItems] }
      : navigation;
  }
}
